import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import { HTMLContent } from '../components/Content';
import { GatsbyImage, getImage, getSrc } from 'gatsby-plugin-image';
import Seo from '../components/Seo';
import { TableOfContents } from '../components/Toc';
import PartnersBox from '../components/PartnersBox';
import Socials from '../components/Socials';
import ArticleMeta from '../components/ArticleMeta';
import {
  Link,
  useTranslation,
  I18nextContext,
} from '@herob191/gatsby-plugin-react-i18next';
import { TopicFromSlug } from '../components/AllTopics';
import { twMerge } from 'tailwind-merge';
import { colorStyle, colorBgStyles } from '../utils';
import { FeaturedSlideshow } from '../components/contentComponents/FeaturedSlideshow';

let activeIntersections = 0;
console.log(`active Intersections: ${activeIntersections}`);

const useOverlapObserver = (setVisibility) => {
  React.useEffect(() => {
    // let toc = document.getElementById('toc-container').getBoundingClientRect();
    // let el = document.getElementById('wrapper');
    let targets = [
      ...document.getElementsByClassName('figure-extra-wide'),
      ...document.getElementsByClassName('figure-left-aligned-out'),
    ];
    const options = {
      root: null,
      rootMargin: '0px 0px -20% 0px',
      threshold: 0,
    };

    let callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeIntersections = activeIntersections + 1;
          console.log(activeIntersections);
          setVisibility(`xl:opacity-0`);
        } else {
          if (activeIntersections > 1) {
            activeIntersections = activeIntersections - 1;
            console.log(activeIntersections);
          } else if (activeIntersections === 1) {
            setVisibility(`xl:opacity-100`);
            activeIntersections = activeIntersections - 1;
            console.log(activeIntersections);
          }
        }
      });
    };

    let io = new IntersectionObserver(callback, options);
    targets.forEach((target) => io.observe(target));

    return () => {
      targets.forEach((target) => io.unobserve(target));
    };
  }, [setVisibility]);
};

// eslint-disable-next-line
export const ArticleTemplate = ({
  content,
  contentComponent,
  description,
  // tags,
  title,
  heroImage,
  featuredExtraOptions,
  featuredCaption,
  featuredVideo,
  topics,
  relatedTopics,
  format,
  partners,
  date,
  toc,
  url,
  slug,
  translationStatus,
  bgColor,
  slideshow,
}) => {
  const ArticleContent = contentComponent;
  const { t } = useTranslation();
  const lng = React.useContext(I18nextContext).language;
  let i18nSlug;
  if (lng === 'id') {
    i18nSlug = slug.substring(3);
  } else {
    i18nSlug = slug;
  }
  const primaryBg = bgColor ? colorStyle(bgColor) : `bg-transparent`;
  const [visibility, setVisibility] = React.useState();
  useOverlapObserver(setVisibility);

  return (
    <section className={twMerge(primaryBg, 'article-section section')}>
      <Seo
        title={title}
        image={getSrc(heroImage)}
        description={description}
        article={true}
      />

      {slideshow && slideshow?.isActive && (
        <div className="relative z-10 mb-[120vh] bg-stone-900">
          <FeaturedSlideshow props={slideshow} />
        </div>
      )}

      <div
        className={twMerge(
          slideshow &&
            slideshow?.isActive && [
              `relative -top-[300vh] -mb-[300vh] h-[300vh]`,
            ],
          `hero-image-container`
        )}
      >
        <div className="hero-image sticky top-12">
          <div className="h-[88svh] overflow-hidden bg-cover bg-fixed bg-center bg-no-repeat brightness-75 transition group-hover:saturate-50">
            {featuredVideo ? (
              <video
                autoplay="true"
                poster={getSrc(heroImage)}
                muted
                loop
                playsInline
                className="h-full w-full object-cover object-center"
              >
                <source src={featuredVideo} type="video/mp4" />
              </video>
            ) : (
              <GatsbyImage
                image={getImage(heroImage)}
                alt=""
                className="h-full"
              />
            )}
          </div>
          <div className="absolute bottom-[10%] w-full text-white">
            <div className="gecko-wrapper">
              <div className="flex justify-between">
                <h2 className="md:text-l text-shadow-sm border-gecko-red border-b-2 font-sans text-xs uppercase lg:text-xl">
                  <TopicFromSlug slug={topics} />
                </h2>
              </div>
              <h1 className="text-shadow-sm font-sans text-4xl leading-tight transition md:text-4xl lg:py-4 lg:text-6xl">
                {title}
              </h1>
              <p className="lg:text-l text-shadow-sm font-serif text-xs font-medium md:text-base lg:font-semibold">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl pt-12">
        <div className="grid grid-cols-12 gap-4">
          {/* article grid */}
          <div className="col-span-2 hidden md:block">
            {/*left sidebar*/}
            <div className="relative z-30 ml-0 hidden text-stone-400 mix-blend-luminosity md:ml-4 md:block xl:hidden">
              <ArticleMeta
                date={date}
                format={format}
                topics={topics}
                relatedTopics={relatedTopics}
                titleText={t('common:article.metaAbout')}
                publishedText={t('common:article.publishedOn')}
                formatText={t('common:article.format')}
                topicText={t('common:article.topics')}
              />
              <PartnersBox
                partners={partners}
                partnersText={t('common:article.partners')}
              />
              <div className="meta-18n meta-box">
                {translationStatus === 'enId' ? (
                  lng === 'id' ? (
                    <Link to={i18nSlug} language="en" className="block w-full">
                      <h1 className="meta-i18nLink">Read in English</h1>
                    </Link>
                  ) : (
                    <Link to={i18nSlug} language="id" className="w-full">
                      <h1 className="meta-i18nLink">
                        Baca dalam Bahasa Indonesia
                      </h1>
                    </Link>
                  )
                ) : null}
              </div>
            </div>
            <div
              className={twMerge(
                visibility,
                'sticky top-24 z-20 ml-0 text-balance mix-blend-luminosity transition-opacity md:ml-4'
              )}
              id="toc-container"
            >
              {toc && toc.length ? (
                <>
                  <TableOfContents
                    nestedHeadings={toc}
                    tocHeader={t('common:article.toc')}
                  />
                  <Socials url={url} title={title} description={description} />
                </>
              ) : null}
            </div>
          </div>
          <div className="order-2 col-span-full md:order-1 md:col-span-8">
            {/* article */}
            <div className="prose">
              <ArticleContent
                content={content}
                className={
                  primaryBg === `bg-transparent` || primaryBg === 'bg-white'
                    ? `article-content mx-auto mb-12 w-11/12`
                    : twMerge(
                        colorBgStyles.darkBackgroundStyle,
                        colorBgStyles.caseStudyStyle,
                        colorBgStyles.detailsStyle,
                        colorBgStyles.paraStyle,
                        colorBgStyles.firstHeaderStyle,
                        'article-content mx-auto mb-12 w-11/12'
                      )
                }
              />
              {featuredCaption && (
                <figcaption className="mt-3 mb-6 border-t-2 border-t-stone-200 pt-2 font-serif text-sm text-stone-600 italic">
                  {featuredCaption}
                </figcaption>
              )}
            </div>
          </div>
          <div className="relative z-30 order-1 col-span-full mx-auto block w-11/12 mix-blend-luminosity md:mx-24 md:hidden md:w-auto xl:order-2 xl:col-span-2 xl:mx-0 xl:block">
            {/* right sidebar */}
            <div className="article-metadata grid grid-cols-3 gap-0 text-stone-400 mix-blend-luminosity md:gap-8 xl:gap-0">
              <ArticleMeta
                date={date}
                format={format}
                topics={topics}
                relatedTopics={relatedTopics}
                titleText={t('common:article.metaAbout')}
                publishedText={t('common:article.publishedOn')}
                formatText={t('common:article.format')}
                topicText={t('common:article.topics')}
              />
              <PartnersBox
                partners={partners}
                partnersText={t('common:article.partners')}
              />
              <div className="meta-18n meta-box">
                {translationStatus === 'enId' ? (
                  lng === 'id' ? (
                    <Link to={i18nSlug} language="en" className="block w-full">
                      <h1 className="meta-i18nLink">Read in English</h1>
                    </Link>
                  ) : (
                    <Link to={i18nSlug} language="id" className="w-full">
                      <h1 className="meta-i18nLink">
                        Baca dalam Bahasa Indonesia
                      </h1>
                    </Link>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ArticleTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
};

const Article = ({ data }) => {
  const { markdownRemark: content } = data;

  return (
    <Layout>
      <ArticleTemplate
        content={content.htmlAst}
        contentComponent={HTMLContent}
        description={content.frontmatter.description}
        tags={content.frontmatter.tags}
        title={content.frontmatter.title}
        heroImage={content.frontmatter.featuredimage}
        featuredExtraOptions={content.frontmatter.headerExtraOptions}
        featuredCaption={content.frontmatter.headerExtraOptions?.headerCaption}
        featuredVideo={
          content.frontmatter.headerExtraOptions?.headerVideo.publicURL
        }
        topics={content.frontmatter.main_topic}
        relatedTopics={content.frontmatter.related_topics}
        format={content.frontmatter.format}
        date={content.frontmatter.date}
        toc={content.headings}
        url={'https://thegeckoproject.org' + content.fields.slug}
        translationStatus={content.frontmatter.translationStatus}
        slug={content.fields.slug}
        bgColor={content.frontmatter.primaryBg}
        ////////////////////////////////////////////
        // TODO remove hardcoding of URL
        ///////////////////////////////////////////

        // sections={content.frontmatter.sections}
        partners={content.frontmatter.partners}
        slideshow={content.frontmatter.featuredSlideshow}
      />
    </Layout>
  );
};

Article.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
};

export default Article;

export const pageQuery = graphql`
  query ArticleByID($id: String!, $language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    markdownRemark(id: { eq: $id }) {
      id
      html
      htmlAst
      fields {
        slug
      }
      headings(depth: h1) {
        id
        title: value
      }
      frontmatter {
        date(formatString: "DD.MM.YYYY")
        title
        description
        main_topic
        related_topics
        format
        translationStatus
        featuredimage {
          childImageSharp {
            gatsbyImageData(
              width: 2000
              quality: 50
              webpOptions: { quality: 70 }
            )
          }
        }
        featuredSlideshow {
          type
          isActive
          slides {
            alt
            content
            position
            image {
              publicURL
              id
            }
          }
        }
        headerExtraOptions {
          headerVideo {
            publicURL
          }
          headerCaption
        }
        partners {
          partnerTitle
          partnerUrl
          partnerLogo {
            publicURL
          }
        }
        primaryBg
      }
    }
  }
`;
